export type UrlQueryField<T> = {
  /** Query key. Defaults to the schema property name. */
  key?: string
  parse: (raw: string | null, params: URLSearchParams) => T
  /** Return undefined to omit the param from the URL. */
  serialize: (value: T) => string | undefined
}

export type UrlQuerySchema<S extends Record<string, unknown>> = {
  [K in keyof S]: UrlQueryField<S[K]>
}

export type UrlQueryPatch<S extends Record<string, unknown>> =
  | Partial<S>
  | ((prev: S) => Partial<S>)

/** Apply patches in order (functional patches see prior merges). */
export function applyUrlQueryPatches<S extends Record<string, unknown>>(
  current: S,
  patches: Array<UrlQueryPatch<S>>
): S {
  let next = current
  for (const patch of patches) {
    const resolved = typeof patch === 'function' ? patch(next) : patch
    next = { ...next, ...resolved }
  }
  return next
}

export function parseUrlQuery<S extends Record<string, unknown>>(
  params: URLSearchParams,
  schema: UrlQuerySchema<S>
): S {
  const result = {} as S
  for (const schemaKey of Object.keys(schema) as Array<keyof S>) {
    const field = schema[schemaKey]
    const paramKey = field.key ?? String(schemaKey)
    result[schemaKey] = field.parse(params.get(paramKey), params)
  }
  return result
}

export function serializeUrlQuery<S extends Record<string, unknown>>(
  current: URLSearchParams,
  schema: UrlQuerySchema<S>,
  state: S,
  dropKeys?: string[]
): URLSearchParams {
  const next = new URLSearchParams(current)
  for (const drop of dropKeys ?? []) {
    next.delete(drop)
  }
  for (const schemaKey of Object.keys(schema) as Array<keyof S>) {
    const field = schema[schemaKey]
    const paramKey = field.key ?? String(schemaKey)
    const serialized = field.serialize(state[schemaKey])
    if (serialized === undefined) {
      next.delete(paramKey)
    } else {
      next.set(paramKey, serialized)
    }
  }
  return next
}

export function stringParam(defaultValue = ''): UrlQueryField<string> {
  return {
    parse: raw => raw ?? defaultValue,
    serialize: value => (!value || value === defaultValue ? undefined : value),
  }
}

export function enumParam<T extends string>(allowed: readonly T[], defaultValue: T): UrlQueryField<T> {
  const allowedSet = new Set<string>(allowed)
  return {
    parse: raw => {
      const value = (raw || '').trim()
      return (allowedSet.has(value) ? value : defaultValue) as T
    },
    serialize: value => (value === defaultValue ? undefined : value),
  }
}

export function enumParamUpper<T extends string>(allowed: readonly T[], defaultValue: T): UrlQueryField<T> {
  const upperToValue = new Map(allowed.map(item => [item.toUpperCase(), item]))
  return {
    parse: raw => upperToValue.get((raw || '').trim().toUpperCase()) ?? defaultValue,
    serialize: value => (value === defaultValue ? undefined : value),
  }
}

/** `1` in the query means on; the param is omitted when off. */
export function flagParam(): UrlQueryField<boolean> {
  return {
    parse: raw => raw === '1' || raw === 'true',
    serialize: value => (value ? '1' : undefined),
  }
}

export function pageParam(): UrlQueryField<number> {
  return {
    parse: raw => {
      const value = Number(raw)
      return Number.isInteger(value) && value >= 1 ? value : 1
    },
    serialize: value => (value <= 1 ? undefined : String(value)),
  }
}

export function optionalIntParam(): UrlQueryField<number | undefined> {
  return {
    parse: raw => {
      const trimmed = (raw || '').trim()
      if (!/^\d+$/.test(trimmed)) return undefined
      return Number(trimmed)
    },
    serialize: value => (value == null ? undefined : String(value)),
  }
}

/** Team editor: `user=new` opens create, `user=<id>` opens edit. */
export type IamUserQuery = number | 'new' | undefined

export function iamUserParam(): UrlQueryField<IamUserQuery> {
  return {
    parse: raw => {
      const trimmed = (raw || '').trim()
      if (trimmed === 'new') return 'new'
      if (!/^\d+$/.test(trimmed)) return undefined
      return Number(trimmed)
    },
    serialize: value => (value == null ? undefined : String(value)),
  }
}

export function optionalStringParam(): UrlQueryField<string> {
  return {
    parse: raw => raw ?? '',
    serialize: value => (value ? value : undefined),
  }
}

export function applyStateAction<T>(current: T, action: T | ((prev: T) => T)): T {
  return typeof action === 'function' ? (action as (prev: T) => T)(current) : action
}
